import { inject, injectable } from 'inversify';
import { action, computed, makeObservable } from 'mobx';
import { TYPES } from '@/engine/ioc';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { ViewsService } from '@/engine/app/Views.service';
import type { UIConfigResolver } from '@/engine/app/modules/ui';
import type { ViewAvailabilityController } from '@/engine/app/modules/ui/UIConfigSchema';
import type { StepsListCommandsFactory } from './commands/StepsListCommands.factory';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { CommandProcessor } from '@/engine/app/modules/core/services/CommandProcessor';
import type { ICommand } from '@/engine/app/modules/core/interfaces/Command.ts';

@injectable()
export class StepsListController {
  private _viewsService: ViewsService;

  private _viewAvailabilityController: ViewAvailabilityController | undefined;

  private _stepsListCommandsFactory: StepsListCommandsFactory;

  private _commandProcessor: CommandProcessor;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.UIConfigResolver) uiConfigResolver: UIConfigResolver,
    @inject(TYPES.StepsListCommandsFactory) stepsListCommandsFactory: StepsListCommandsFactory,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: CommandProcessor
  ) {
    this._stepsListCommandsFactory = stepsListCommandsFactory;
    this._commandProcessor = commandProcessor;

    makeObservable(this);

    this._viewAvailabilityController = uiConfigResolver.getOptionalValueByDescriptor('viewAvailabilityController');

    this._viewsService = viewsService;
  }

  @computed
  public get views(): Array<ViewEntity> {
    return this._viewsService.views;
  }

  @computed
  public get currentViewIndex(): number {
    return this._viewsService.views.findIndex((view: ViewEntity) => {
      return (
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        view.getDescriptor() === this._viewsService.currentView!.getDescriptor()
      );
    });
  }

  @computed
  public get getCurrentView(): ViewEntity | null {
    return this._viewsService.currentView;
  }

  @action
  public async setCurrentView(view: ViewEntity): Promise<void> {
    const command: ICommand = this._stepsListCommandsFactory.createSetStepCommand(view);
    await this._commandProcessor.execute(command);
    this._viewsService.currentView?.setApprovedStatus(false); // [chebotarev] manual visiting of the step => unapprove it
  }

  public isViewEnabled(descriptor: string): boolean {
    if (!this._viewAvailabilityController) {
      return true;
    }

    if (this._isDisabledByPreviousView(descriptor)) {
      return false;
    }

    return this._viewAvailabilityController.isViewEnabled(descriptor);
  }

  private _isDisabledByPreviousView(descriptor: string): boolean {
    const descriptorIdx = this.views.findIndex((view) => view.getDescriptor() === descriptor);
    if (descriptorIdx === 0) {
      return false;
    }

    const previousDescription = this.views[descriptorIdx - 1].getDescriptor();
    if (!this.isViewEnabled(previousDescription)) {
      return true;
    }

    return false;
  }
}
